import * as React from 'react';

import { StyleSheet, css } from '@coursera/coursera-ui';

import type { CtaLine } from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentful';
import CTAContentfulPiece from 'bundles/coursera-plus-landing-page/components/ctaContentful/CTAContentfulPiece';
import {
  CTA_LOCATIONS,
  PRESENTATIONS,
  PRODUCT_CHOICES,
  isPieceForCheckout,
  isPresentationButton,
} from 'bundles/coursera-plus-landing-page/contentful/constants';
import type { CourseraPlusCtaPartPicked } from 'bundles/coursera-plus-landing-page/contentful/fetchContentful';

const styles = StyleSheet.create({
  line: {
    marginBottom: '12px',
    fontSize: '14px',
  },
});

export type LineProps = {
  line: CtaLine;
  ctaLocation: string;
};

export type ConnectedMessage = {
  entryTitle?: string | null;
  pretranslated?: string | null;
  freeform?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  freeformRich?: any | null;
  displayAsBold?: boolean | null;
  hideInLocation?: string | null;
  size?: string | null;
};

export type ConnectedPiece = {
  product: string;
  presentation: string;
  messages: Array<ConnectedMessage>;
  displayAsBold?: boolean;
  hideInLocation?: string | null;
  size?: string;
  isFirstCheckoutInHero: boolean;
};

const productToPass = (product: $TSFixMe) => product ?? PRODUCT_CHOICES.primary;
const presentationToPass = (presentation: $TSFixMe) => presentation ?? PRESENTATIONS.plain;
const displayAsBoldToPass = (displaysAsBold: $TSFixMe) => displaysAsBold ?? false;
const sizeToPass = (size: $TSFixMe, isHeader: $TSFixMe) => (isHeader ? size ?? 'none' : 'none');

const buildConnectedPieces = (
  pieces: Array<CourseraPlusCtaPartPicked>,
  isHeader: boolean,
  firstLineWithCheckout: boolean
): Array<ConnectedPiece> => {
  const newConnected = (nextPiece: CourseraPlusCtaPartPicked): ConnectedPiece => {
    const {
      hideInLocation,
      product,
      presentation,
      entryTitle,
      pretranslated,
      freeform,
      freeformRich,
      displayAsBold,
      size,
    } = nextPiece;
    return {
      product: productToPass(product),
      presentation: presentationToPass(presentation),
      displayAsBold: displayAsBoldToPass(displayAsBold),
      size: sizeToPass(size, isHeader),
      messages: [{ entryTitle, pretranslated, freeform, freeformRich, displayAsBold, hideInLocation }],
      isFirstCheckoutInHero: false,
    };
  };
  const returnValue: Array<ConnectedPiece> = [];
  let currentConnected: ConnectedPiece | null = null;
  for (const piece of pieces) {
    const {
      hideInLocation,
      product,
      presentation,
      entryTitle,
      pretranslated,
      freeform,
      freeformRich,
      displayAsBold,
      size,
    } = piece;
    if (currentConnected === null) {
      currentConnected = newConnected(piece);
    } else if (
      currentConnected.product === productToPass(product) &&
      currentConnected.presentation === presentationToPass(presentation)
    ) {
      currentConnected.messages.push({
        entryTitle,
        pretranslated,
        freeform,
        freeformRich,
        displayAsBold,
        size,
        hideInLocation,
      });
    } else {
      returnValue.push(currentConnected);
      currentConnected = newConnected(piece);
    }
  }
  if (currentConnected) {
    returnValue.push(currentConnected);
  }
  if (isHeader && firstLineWithCheckout) {
    for (let i = 0; i < returnValue.length; i += 1) {
      if (isPieceForCheckout(returnValue[i])) {
        returnValue[i].isFirstCheckoutInHero = true;
        break;
      }
    }
  }
  return returnValue;
};

const CTAContentfulLine = (props: LineProps) => {
  const { line, ctaLocation } = props;
  const bottomMargin = line.pieces.reduce((finalBottomMargin, piece) => {
    return Math.max(finalBottomMargin, piece.bottomMargin ?? 0);
  }, 0);
  const topMargin = line.pieces.reduce((finalTopMargin, piece) => {
    return Math.max(finalTopMargin, piece.topMargin ?? 0);
  }, 0);
  const spacingStyle = { marginTop: `${topMargin}px`, marginBottom: `${bottomMargin === 0 ? 12 : bottomMargin}px` };
  if (ctaLocation === CTA_LOCATIONS.sticky) {
    spacingStyle.marginBottom = '0';
  } else if (ctaLocation === CTA_LOCATIONS.hero && line.pieces[0].presentation === PRESENTATIONS.bigButton) {
    spacingStyle.marginBottom = '0';
  }
  const connected = buildConnectedPieces(line.pieces, ctaLocation === CTA_LOCATIONS.hero, line.firstLineWithCheckout);
  const singleBigButton = connected.length === 1 && connected[0].presentation === PRESENTATIONS.bigButton;
  let key = 0;
  return (
    // @ts-expect-error FIXME result of fixing typing on coursera-ui aphrodite exports in PR#45351
    <div className="rc-CTAContentfulLine" {...css(styles.line)} style={spacingStyle}>
      {connected.map((piece, index) => {
        key += 1;
        let separator;
        if (index > 0) {
          const prevPiece = connected[index - 1];
          // bigger space between buttons and text
          const needWideSeparator =
            isPresentationButton(prevPiece.presentation) || isPresentationButton(piece.presentation);
          separator = needWideSeparator ? (
            <div className="wideSeparator" key={'spacediv' + key} style={{ display: 'inline-block', width: '22px' }} />
          ) : (
            <span className="narrowSeparator" key={'spacespan' + key}>
              {' '}
            </span>
          );
        }
        return (
          <React.Fragment key={'fragment' + key}>
            {index > 0 && separator}
            <CTAContentfulPiece piece={piece} ctaLocation={ctaLocation} key={key} singleBigButton={singleBigButton} />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default CTAContentfulLine;
